<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-02-26 (0.1.0) (jhoward)
 *   - Add new Offer base component for displaying offer details, images, titles, subtitles, body, and disclaimers.
 *   - Provide slot for custom actions.
 *
 * 2026-02-28 (0.2.0) (jhoward)
 *   - Add DisplayPrizes component to offer details.
 *   - Add View More button linking to offer view route.
 *
 * 2026-03-02 (0.2.1) (jhoward)
 *   - Refactor offer display to use PeppermillCard with slots for body and actions.
 *
 * 2026-03-11 (0.3.0) (jhoward)
 *   - Add REDEEM and CANCEL actions to offers.
 *   - Integrate OfferManagementDialog for offer actions.
 *
 * 2026-03-13 (0.4.0) (jhoward)
 *   - Replace RedeemOfferDialog with OfferManagementDialog.
 *   - Trigger dialog via appStore.offerManagementDialog.offer.
 *
 * 2026-03-13 (0.5.0) (jhoward)
 *   - Add computed offerImageUrl to select local offer image.
 *   - Update PeppermillCard image-url prop to use offerImageUrl.
 *   - Remove unused CSS for .offer--image.
 *
 * 2026-03-13 (0.5.1) (jhoward)
 *   - Refactor to use appStore.getOfferImageUrl for offer image URL instead of inline logic.
 *
 * 2026-03-13 (0.5.2) (jhoward)
 *   - Use '400' as offer image size in appStore.getOfferImageUrl.
 *   - Show check_circle icon for accepted offers.
 *
 * 2026-03-17 (0.6.0) (jhoward)
 *   - Add 'BOOK ROOM' button for offers with RESNET_OFFER_CODE and ACTIVE status, using validateHotelOffer.
 *   - Add isOfferRedeemed and isOfferSoldOut helpers for UI state.
 *   - Update accepted offer icon logic to use isOfferRedeemed.
 *   - Add new imports and supporting logic for booking validation.
 *
 * 2026-03-24 (0.7.0) (jhoward)
 *   - Update logic for BOOK ROOM and REDEEM buttons to be mutually exclusive based on offer properties.
 *   - Center card title and subtitle.
 *
 * 2026-03-30 (0.7.1) (jhoward)
 *   - Remove inline validateHotelOffer function and import from helper.
 *   - No functional change for consumers.
 *
 * 2026-04-09 (0.8.0) (jhoward)
 *   - Add import and usage of src\helpers\isOfferForLinkedAccount for linked account detection.
 *   - Disable BOOK ROOM action for linked account offers and show warning via appStore.addSnack.
 *   - Add visual indicator (material-icons) for linked account offers.
 *   - Update CSS for new linked account indicator.
 *
 * 2026-09-08 (0.9.0) (jhoward)
 *   - Replace the floating check_circle icon with App\components\offers\OfferRedeemedStatus.vue in the card body, so a
 *     redeemed offer says so in words - including who redeemed it and which prize was taken - rather than relying on a
 *     hover title a touch device never shows.
 *   - Fade and desaturate the card image of a redeemed offer so the offers still open to the guest carry the grid.
 *     The image only: the text stays at full contrast.
 *   - Remove the .offer--accepted rules, and with them the '.offer--accepted + .offer--linked_account' stacking hack
 *     that existed only because the two corner badges overlapped.
 *
 * 2026-09-09 (0.10.0) (jhoward)
 *   - Add App\components\offers\OfferExpiryStatus.vue beside the redeemed badge. OFFER_APPLICABLE_END_TS was on every
 *     offer and rendered nowhere, so a guest had no way to tell which offers were about to lapse.
 *   - Add App\components\offers\OfferCalendarLinks.vue to the actions of a redeemed offer that has a real date.
 *
 * 2026-09-09 (0.11.0) (jhoward)
 *   - Move App\components\offers\OfferCalendarLinks.vue out of the actions slot and into .offer--status_row, where it
 *     now renders one 32px pill rather than two floated action links.
 *     Measured on a redeemed offer with a prize: at the card's 800px maximum the four links did fit on one line, so
 *     the win there is hierarchy rather than height - two CTAs at full weight instead of four things that all looked
 *     like one. At the offers grid's 360px floor, which is where the cards actually sit, the links wrapped the actions
 *     block to three rows, 124px; it is now 52px, and the card is 36px shorter overall. The pill takes its own line in
 *     the status row at that width, which is what the 36px rather than 72px is: 32px of pill inside a 38px badge row
 *     when it fits beside the badge, a wrapped line when it does not. Either way it is out of the zone a guest scans
 *     for what to do next.
 *
 * 2026-09-23 (0.12.0) (jhoward)
 *   - Add App\components\offers\OfferPendingStatus.vue to the front of .offer--status_row, so an admin can tell a
 *     PENDING offer from a live one at a glance. hasStatus widens to match, or a pending offer with no other status
 *     would render no row to put the badge in.
 *
 * 2026-11-02 (0.13.0) (jhoward)
 *   - Clicking the offer image opens the offer's view page, the same place VIEW DETAILS goes. PeppermillCard draws the
 *     image as a background div with no slot or event of its own, so the click is caught on the card and acted on only
 *     when it lands on .card--image. Pointer-only: VIEW DETAILS stays the keyboard and screen-reader route, so the
 *     image is not made a second tab stop.
 */

import PeppermillCard from '@framework/components/layout/PeppermillCard.vue';
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import OfferCalendarLinks from '@/components/offers/OfferCalendarLinks.vue';
import OfferExpiryStatus from '@/components/offers/OfferExpiryStatus.vue';
import OfferPendingStatus from '@/components/offers/OfferPendingStatus.vue';
import OfferRedeemedStatus from '@/components/offers/OfferRedeemedStatus.vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getOfferExpiry from '@/helpers/getOfferExpiry';
import isOfferForLinkedAccount from '@/helpers/isOfferForLinkedAccount';
import isOfferPending from '@/helpers/isOfferPending';
import isOfferRedeemed from '@/helpers/isOfferRedeemed';
import isOfferSoldOut from '@/helpers/isOfferSoldOut';
import validateHotelOffer from '@/helpers/validateHotelOffer';
import useAppStore from '@/store';

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
});

const appStore = useAppStore();
const router = useRouter();
const viewOfferPath = computed(
  () => '/offers/' + props.offer.CASINO_OFFER_DOMAIN + '/' + props.offer.CASINO_OFFER_CODE + '/view',
);

function onCardClick(event: MouseEvent) {
  if ((event.target as HTMLElement | null)?.closest('.card--image')) {
    router.push(viewOfferPath.value);
  }
}
const offerImageUrl = appStore.getOfferImageUrl(props.offer, '400');
const isRedeemed = computed(() => isOfferRedeemed(props.offer));
//PeppermillCard only renders its body wrapper when the slot is filled, so keep the slot conditional rather than
//emitting an empty padded div on every offer that has neither status.
//The calendar control lives in that row too, gated on isRedeemed, which already forces this true. If that gate ever
//widens to unredeemed dated offers, widen this with it or the row will not render at all.
const hasStatus = computed(
  () => isOfferPending(props.offer) || isRedeemed.value || getOfferExpiry(props.offer).hasExpiry,
);
</script>

<template>
  <peppermill-card
    class="offer--container"
    :class="{ 'offer--container-redeemed': isRedeemed }"
    :title="offer.OFFER_COPY_TITLE"
    :subtitle="offer.OFFER_COPY_SUBTITLE"
    :image-url="offerImageUrl"
    @click="onCardClick"
  >
    <template v-if="hasStatus" v-slot:body>
      <div class="offer--status_row">
        <offer-pending-status :offer="offer"></offer-pending-status>
        <offer-redeemed-status :offer="offer"></offer-redeemed-status>
        <offer-expiry-status :offer="offer"></offer-expiry-status>
        <offer-calendar-links v-if="isRedeemed" :offer="offer"></offer-calendar-links>
      </div>
    </template>
    <template v-slot:actions>
      <slot name="actions"></slot>
      <router-link :to="viewOfferPath">VIEW DETAILS</router-link>
      <template
        v-if="
          offer?.OFFER_CAN_BE_REDEEMED === 'YES' &&
          offer?.RESNET_OFFER_CODE.length &&
          offer?.OFFER_ACCEPT_STATUS === 'ACTIVE'
        "
      >
        <a
          v-if="offer.CASINO_PATRON_ID === appStore.state.user?.account_id"
          href="#"
          @click.prevent="validateHotelOffer(offer)"
          >BOOK ROOM</a
        >
        <a
          v-else
          class="layout--action_button-disabled"
          title="Cannot book offers for linked accounts"
          href="#"
          @click.prevent="
            appStore.addSnack({
              duration: 10000,
              message: 'You must login to ' + offer.CASINO_PATRON_FULL_NAME + '\'s account to book this offer.',
              icon: 'warning',
            })
          "
          >BOOK ROOM</a
        >
      </template>
      <a
        v-else-if="offer?.OFFER_CAN_BE_REDEEMED === 'YES' && !offer?.RESNET_OFFER_CODE.length"
        @click.stop.prevent="appStore.offerManagementDialog.offer = offer"
        href="#"
        >REDEEM</a
      >
      <a
        v-else-if="'YES' === offer.OFFER_CAN_BE_VOIDED"
        href="#"
        @click.stop.prevent="appStore.offerManagementDialog.offer = offer"
        >CANCEL</a
      >
      <a v-else-if="offer && isOfferSoldOut(offer)" class="layout--action_button-disabled" @click.prevent href="#"
        >SOLD OUT</a
      >

      <span
        v-if="isOfferForLinkedAccount(offer)"
        class="offer--linked_account material-icons"
        :title="'This offer is for ' + offer.CASINO_PATRON_FULL_NAME"
        >attribution</span
      >
    </template>
  </peppermill-card>
</template>

<style scoped>
.offer--container {
  position: relative;
  max-width: 800px;
}

.offer--container:deep(.card--title) {
  font-size: 2em;
}

.offer--container:deep(.card--title),
.offer--container:deep(.card--subtitle) {
  border-bottom: none;
  text-align: center;
}

.offer--container:deep(.card--image) {
  cursor: pointer;
}

.offer--status_row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25em 0.5em;
}

.offer--linked_account {
  color: rgb(var(--primary-color));
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -30%);
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 2px 2px 8px 0 rgb(0 0 0 / 35%);
  z-index: 1;
  font-size: 2.5rem;
}

/*A redeemed offer is finished, so it steps back and lets the actionable cards carry the grid. The image alone -
  dimming the card would take the title, the body and the status badge down with it for no benefit.*/
.offer--container-redeemed:deep(.card--image) {
  filter: grayscale(0.85);
  opacity: 0.7;
}

/*Desaturating to signal state is exactly what this asks us not to do, so the image comes back at full strength and
  the badge is left to say it in words.*/
@media (prefers-contrast: more) {
  .offer--container-redeemed:deep(.card--image) {
    filter: none;
    opacity: 1;
  }
}
</style>
